// Vertical bar chart of daily USD spend. Click a bar to drill down.

import { useMemo } from "react";

import type { SpendBucket } from "@/services/api/spend";
import { cn } from "@/utils/cn";

interface Props {
  buckets: SpendBucket[];
  selectedDay: string | null;
  onSelectDay: (day: string) => void;
}

export function DailySpendChart({ buckets, selectedDay, onSelectDay }: Props) {
  const max = useMemo(() => {
    const values = buckets.map((b) => parseFloat(b.cost_usd));
    return Math.max(0.01, ...values);
  }, [buckets]);

  if (buckets.length === 0) {
    return (
      <div className="rounded border border-ruleline bg-parchment-50 p-8 text-center">
        <p className="text-sm italic text-warmgray-500">
          No spend recorded yet this period.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded border border-ruleline bg-parchment-50 p-4">
      <div className="flex items-end gap-1.5" style={{ height: 180 }}>
        {buckets.map((b) => {
          const value = parseFloat(b.cost_usd);
          const heightPct = Math.max(2, (value / max) * 100);
          const isSelected = selectedDay === b.bucket;
          const day = b.bucket.slice(8, 10);
          return (
            <button
              key={b.bucket}
              type="button"
              onClick={() => onSelectDay(b.bucket)}
              className={cn(
                "group relative flex-1 min-w-[14px] rounded-sm transition-colors",
                isSelected
                  ? "bg-oxblood-500"
                  : "bg-warmgray-300 hover:bg-oxblood-400",
              )}
              style={{ height: `${heightPct}%` }}
              title={`${b.bucket}: $${value.toFixed(4)} · ${b.tool_calls} tool calls · ${b.trace_count} traces`}
            >
              <span className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-espresso-700 px-1.5 py-0.5 text-[10px] text-parchment-50 opacity-0 group-hover:opacity-100">
                ${value.toFixed(2)}
              </span>
              <span className="pointer-events-none absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] text-warmgray-500">
                {day}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
